// filter-shortcuts.js — Quick-filter button row above the notes list.
//
// Adds four built-in date-range filters (Today / WTD / MTD / YTD) and any
// number of user-defined custom filters, supplied by the Settings store as a
// validated [{label, query}, …] array. The active filter combines with — but
// does not modify — the search box, so the user can layer search terms on top
// of a filter.
//
// All buttons (built-ins + customs) live as direct children of
// #filter-shortcuts in a single ordered track. The order is driven by the
// synced `filter_order` Settings field (array of runtime DOM ids) so the
// user's arrangement travels across devices; drag-to-rearrange (desktop
// click-and-drag, mobile long-press-then-drag) writes back to that field.
//
// Pure module.

import { Settings } from '../settings/settings-store.js';
import { updateFileList } from './file-list.js';
import {
  BUILTIN_FILTERS,
  CUSTOM_PREFIX,
  getActiveFilterId,
  getActiveFilterPredicate,
  getCustomFilters,
  setActiveFilterId,
  setCustomFiltersState,
} from './filter-state.js';

const _LS_KEY = 'notes_active_filter';

// The active-filter id and the parsed custom-filter list (plus the predicate
// derived from them) now live in the filter-state.js leaf so file-list.js can
// import getActiveFilterPredicate directly without the old window dodge. This
// module owns the DOM/wiring and keeps the leaf's state in sync; it reads the
// active id via getActiveFilterId() and writes it via setActiveFilterId().
let _container: any = null;

// Drag-controller state. Cross-tab `filter_order` subscriptions skip
// re-rendering while `_dragInProgress` is true so a remote update can't
// yank the DOM out from under the user's finger. `_suppressNextClick`
// silences the synthesized click that fires on the originating button
// after a drag-then-release on desktop — without it the drop would also
// toggle the filter.
let _dragInProgress = false;
let _suppressNextClick = false;

/**
 * @param {(ls: Storage) => any} fn
 * @returns {any}
 */
function _safeLS(fn: any) {
  try {
    return fn(localStorage);
  } catch (_e: any) {
    /* private mode / sandboxed */
  }
}

/** @returns {void} */
function _updateButtonStates() {
  if (!_container) return;
  const activeId = getActiveFilterId();
  for (const btn of _container.querySelectorAll('button[data-filter-id]')) {
    btn.classList.toggle('active', btn.dataset.filterId === activeId);
    btn.setAttribute('aria-pressed', btn.dataset.filterId === activeId ? 'true' : 'false');
  }
}

/**
 * @param {any} id - the filter id to toggle.
 * @returns {void}
 */
function setActiveFilter(id: any) {
  const next = getActiveFilterId() === id ? null : id;
  setActiveFilterId(next);
  _safeLS((ls: any) => (next ? ls.setItem(_LS_KEY, next) : ls.removeItem(_LS_KEY)));
  _updateButtonStates();
  updateFileList();
}

/**
 * @param {any} id
 * @param {string} label
 * @returns {HTMLButtonElement}
 */
function _makeButton(id: any, label: any) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.filterId = id;
  btn.textContent = label;
  btn.setAttribute('aria-pressed', 'false');
  btn.addEventListener('click', () => {
    // A drag-then-release on desktop synthesizes a click on the originating
    // button; swallow exactly one such click so the drop doesn't double as
    // a filter toggle.
    if (_suppressNextClick) {
      _suppressNextClick = false;
      return;
    }
    setActiveFilter(id);
  });
  return btn;
}

// Resolve the display order: start from the saved `filter_order` (runtime
// DOM ids), drop ids that no longer correspond to a known button (custom
// renamed/deleted, future built-in removals), then append any known id not
// in the saved order with built-ins first then customs as the default tail.
/** @returns {string[]} */
function _resolvedOrder(): string[] {
  const customs = getCustomFilters();
  const known = new Set<string>([
    ...BUILTIN_FILTERS.map((f: any) => f.id),
    ...customs.map((c: any) => c.id),
  ]);
  const saved = (Settings?.get('filter_order', []) || []) as any[];
  const seen = new Set<string>();
  const out: string[] = [];
  if (Array.isArray(saved)) {
    for (const id of saved) {
      if (typeof id !== 'string') continue;
      if (!known.has(id) || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
  }
  for (const f of BUILTIN_FILTERS) if (!seen.has(f.id)) out.push(f.id);
  for (const c of customs) if (!seen.has(c.id)) out.push(c.id);
  return out;
}

/** @returns {void} */
function _renderAll() {
  if (!_container) return;
  const customs = getCustomFilters();
  const labelById = new Map<string, string>();
  for (const f of BUILTIN_FILTERS) labelById.set(f.id, f.label);
  for (const c of customs) labelById.set(c.id, c.label);

  _container.innerHTML = '';
  for (const id of _resolvedOrder()) {
    const label = labelById.get(id);
    if (label == null) continue;
    _container.appendChild(_makeButton(id, label));
  }

  const activeId = getActiveFilterId();
  if (activeId?.startsWith(CUSTOM_PREFIX) && !customs.some((f: any) => f.id === activeId)) {
    setActiveFilterId(null);
    _safeLS((ls: any) => ls.removeItem(_LS_KEY));
  }
  _updateButtonStates();
}

/** @returns {void} */
function _saveOrder() {
  if (!_container || !Settings) return;
  const ids = [...(_container.querySelectorAll('button[data-filter-id]') as any)].map(
    (b: any) => b.dataset.filterId
  );
  // Fire-and-forget — the in-memory snapshot is updated synchronously, so a
  // _renderAll right after still sees the new order; the async write
  // catches up.
  Settings.set('filter_order', ids).catch((e: any) =>
    console.warn('[filter-shortcuts] persist filter_order failed:', e)
  );
}

// Array-based setter driven by the Settings store: callers supply a validated
// [{label, query}, …] list and the row repaints synchronously.
/**
 * @param {any} rows - validated [{ label, query }, …].
 * @returns {void}
 */
function setCustomFilters(rows: any) {
  if (!_container) return;
  if (!Array.isArray(rows)) return;
  const out = [];
  const seen = new Set();
  for (const r of rows) {
    if (!r?.label || !r.query) continue;
    const id = CUSTOM_PREFIX + r.label;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label: r.label, query: r.query });
  }
  setCustomFiltersState(out);
  _renderAll();
}

// Translate a vertical wheel gesture into horizontal scroll when the filter
// row overflows. Mirrors the deltaMode-normalisation pattern in
// editor/table-features.js. If the row fits, leave the event alone so the
// rest of the page can still scroll vertically.
/**
 * @param {WheelEvent} e
 * @returns {void}
 */
function _onFilterWheel(e: any) {
  if (!_container) return;
  if (_container.scrollWidth <= _container.clientWidth) return;
  // Already a horizontal gesture (trackpad swipe) — let the browser handle it.
  if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
  const LINE_HEIGHT_PX = 40;
  const raw = e.deltaY;
  const normalized =
    e.deltaMode === 1 ? raw * LINE_HEIGHT_PX : e.deltaMode === 2 ? raw * window.innerHeight : raw;
  _container.scrollLeft += normalized;
  e.preventDefault();
}

// Wire click-and-drag (desktop) / long-press-and-drag (mobile) reordering.
// The slot algorithm mirrors mobile-keyboard-accessory.ts: snapshot every
// button's center once at drag start, translate the dragged button by `dx`,
// and only update sibling transforms when the target slot crosses a
// neighbour's center.
/**
 * @param {HTMLElement} container
 * @returns {void}
 */
function _setupDrag(container: any) {
  // { btn, fromIndex, toIndex, startX, slots, spacing }
  let drag: any = null;
  // { x, y, btn, ptrId, coarse }
  let press: any = null;
  let pressTimer: any = null;

  const buttonsOf = (): any[] => [...container.querySelectorAll('button[data-filter-id]')] as any[];

  function startDrag(btn: any, clientX: number, ptrId: number): void {
    const els = buttonsOf();
    if (els.length < 2) return;
    const slots = els.map((el: any) => {
      const r = el.getBoundingClientRect();
      return { el, center: r.left + r.width / 2 };
    });
    const fromIndex = els.indexOf(btn);
    if (fromIndex < 0) return;
    const spacing = slots.length > 1 ? slots[1].center - slots[0].center : 0;
    drag = { btn, fromIndex, toIndex: fromIndex, startX: clientX, slots, spacing };
    _dragInProgress = true;
    container.classList.add('bar-dragging');
    btn.classList.add('dragging');
    try {
      btn.setPointerCapture(ptrId);
    } catch {
      /* capture can fail mid-gesture; container-level listeners still track it */
    }
  }

  function moveDrag(clientX: number): void {
    if (!drag) return;
    const { slots, fromIndex, spacing, btn } = drag;
    const n = slots.length;
    const dx = clientX - drag.startX;
    btn.style.transform = `translateX(${dx}px) scale(1.08)`;
    const dc = slots[fromIndex].center + dx;
    let to = fromIndex;
    while (to < n - 1 && dc > slots[to + 1].center) to++;
    while (to > 0 && dc < slots[to - 1].center) to--;
    if (to === drag.toIndex) return;
    drag.toIndex = to;
    slots.forEach((s: any, d: number) => {
      if (s.el === btn) return;
      let shift = 0;
      if (fromIndex < to && d > fromIndex && d <= to) shift = -spacing;
      else if (fromIndex > to && d >= to && d < fromIndex) shift = spacing;
      s.el.style.transform = shift ? `translateX(${shift}px)` : '';
    });
  }

  function endDrag(): void {
    if (!drag) return;
    const { slots, fromIndex, toIndex, btn } = drag;
    const els = slots.map((s: any) => s.el);
    const [moved] = els.splice(fromIndex, 1);
    els.splice(toIndex, 0, moved);
    slots.forEach((s: any) => {
      s.el.style.transform = '';
    });
    btn.classList.remove('dragging');
    container.classList.remove('bar-dragging');
    els.forEach((el: any) => {
      container.appendChild(el);
    });
    const moved2 = fromIndex !== toIndex;
    drag = null;
    _dragInProgress = false;
    if (moved2) {
      _suppressNextClick = true;
      _saveOrder();
    }
  }

  container.addEventListener('pointerdown', (e: any) => {
    if (e.button !== undefined && e.button !== 0) return;
    const btn = (e.target as any).closest?.('button[data-filter-id]');
    if (!btn) return;
    if (buttonsOf().length < 2) return;
    const coarse = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    press = { x: e.clientX, y: e.clientY, btn, ptrId: e.pointerId, coarse };
    if (coarse) {
      // Mobile: 500ms long-press to enter drag mode. A pre-threshold move
      // cancels so the browser's native horizontal scroll on the
      // overflow-x container still works.
      pressTimer = setTimeout(() => {
        pressTimer = null;
        if (!press) return;
        startDrag(press.btn, press.x, press.ptrId);
      }, 500);
    }
    // Fine pointer: no timer; drag starts in pointermove once the 5px
    // threshold is crossed. Below the threshold the native click fires and
    // toggles the filter.
  });

  container.addEventListener('pointermove', (e: any) => {
    if (drag) {
      e.preventDefault();
      moveDrag(e.clientX);
      return;
    }
    if (!press) return;
    const dx = e.clientX - press.x;
    const dy = e.clientY - press.y;
    if (press.coarse) {
      // Cancel pending long-press if the finger moved before it fires.
      if (pressTimer && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        clearTimeout(pressTimer);
        pressTimer = null;
        press = null;
      }
    } else {
      // Desktop: 5px movement = drag.
      if (dx * dx + dy * dy > 25) {
        startDrag(press.btn, press.x, press.ptrId);
        moveDrag(e.clientX);
      }
    }
  });

  const end = (): void => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }
    press = null;
    if (drag) endDrag();
  };
  container.addEventListener('pointerup', end);
  container.addEventListener('pointercancel', end);
}

/** @returns {void} */
function initFilterShortcuts() {
  _container = document.getElementById('filter-shortcuts');
  if (!_container) return;
  _renderAll();
  _container.addEventListener('wheel', _onFilterWheel, { passive: false });
  _setupDrag(_container);
  setActiveFilterId(_safeLS((ls: any) => ls.getItem(_LS_KEY)) || null);
  _updateButtonStates();
  // Repaint when the saved order changes from another tab/device. Skip
  // during an active drag — the in-progress gesture owns the DOM, and the
  // value will be re-read on the next render after endDrag.
  Settings?.subscribe?.('filter_order', () => {
    if (_dragInProgress) return;
    _renderAll();
  });
}

const FilterShortcuts = {
  initFilterShortcuts,
  getActiveFilterPredicate,
  setCustomFilters,
  setActiveFilter,
};

// The former window.* back-compat shims (FilterShortcuts, initFilterShortcuts,
// getActiveFilterPredicate, setCustomFilters, setActiveFilter) had no remaining
// readers — every caller (app-init, settings/sections/filters, file-list via
// filter-state) imports the ESM names — so they are gone.

export {
  FilterShortcuts,
  getActiveFilterPredicate,
  initFilterShortcuts,
  setActiveFilter,
  setCustomFilters,
};
